import { MAX_MEDIA_SIZE } from "constants/form"
import { receiveErrors } from "modules/errors/reducer"
import { errorFieldsSelector } from "modules/errors/selectors"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { modelSelector } from "store/selectors"
import { Spin, message } from "antd"
import { CustomInputError } from "ui/CustomInputError"
import { MediaGrid } from "ui/MediaGrid/SortableMediaGrid"
import { MediaUpload } from "ui/MediaUpload"
import { DragAndDrop } from "ui/PersonalInfo/ProfileImages/DragAndDrop"
import { VideoPlayerModal } from "ui/VideoPlayerModal"
import { isMediaFileVideo } from "utils/media-upload"
import MediaEditorModal from "ui/MediaEditorModal"
import { buildMediaPayload } from "../mappers/toApiPayload"
import { useWizardContext } from "../WizardContext"
import style from "pagesV2/pro/Experiences/ListingMedia/ListingMedia.module.css"

const MAX_COUNT = 10

/**
 * Media step — photo/video upload, reorder, crop, trim.
 *
 * localMedia is stored in WizardContext.mediaFilesRef so it survives step remounts
 * (File objects cannot go into Redux or AntD form store).
 *
 * Save handler validates ≥ 2 active media items before dispatching.
 */
export const MediaStep = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { stepSaveHandlersRef, stepValidateHandlersRef, mediaFilesRef } = useWizardContext()
  const trip = useSelector(modelSelector("trip"))
  const errors = useSelector(errorFieldsSelector())

  const [isLoading, setIsLoading] = useState(false)
  const [videoModalOpen, setVideoModalOpen] = useState(false)
  const [cropModalOpen, setCropModalOpen] = useState(false)
  const [selectedMedia, setSelectedMedia] = useState(null)
  const [mediaToCrop, setMediaToCrop] = useState(null)

  // Initialize localMedia from the context ref (persisted across remounts) or trip data
  const [localMedia, setLocalMedia] = useState(() => {
    if (mediaFilesRef.current.length > 0) return mediaFilesRef.current

    const allMedia = [
      ...(trip?.photos || []),
      ...(trip?.cropped_photos || []),
      ...(trip?.trimmed_videos || []),
      ...(trip?.videos || [])
    ]
    return allMedia.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  })

  // Keep context ref in sync for remount persistence
  useEffect(() => {
    mediaFilesRef.current = localMedia
  }, [localMedia, mediaFilesRef])

  // Re-sync from trip if trip changes and we have no local edits (no new File objects)
  useEffect(() => {
    if (!trip) return
    const hasLocalFiles = localMedia.some((m) => m.file instanceof File)
    if (!hasLocalFiles) {
      const allMedia = [
        ...(trip?.photos || []),
        ...(trip?.cropped_photos || []),
        ...(trip?.trimmed_videos || []),
        ...(trip?.videos || [])
      ].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      setLocalMedia(allMedia)
    }
  }, [trip]) // eslint-disable-line react-hooks/exhaustive-deps

  const filteredMedia = localMedia.filter((m) => !m._destroy)
  const activeMediaLength = filteredMedia.length
  const hasMedia = activeMediaLength > 0

  const onFileListChange = (media) => {
    setLocalMedia((prev) => [...prev, ...media])
    dispatch(receiveErrors({ data: { message: { listing_media: [] } }, showToast: false }))
  }

  const handleDelete = (media) => {
    if (media?.id && typeof media.id === "number") {
      setLocalMedia((prev) => {
        const updated = prev.map((item) => (item.id === media.id ? { ...item, _destroy: true } : item))
        const deletedIdx = updated.findIndex((item) => item.id === media.id)
        return updated.map((item, index) =>
          index > deletedIdx && !item._destroy ? { ...item, position: item.position - 1 } : item
        )
      })
    } else {
      const pos = localMedia.find((item) => item.id === media.id)?.position
      setLocalMedia((prev) =>
        prev
          .filter((item) => item.id !== media.id)
          .map((item) => (item.position > pos ? { ...item, position: item.position - 1 } : item))
      )
    }
  }

  const handleReorder = (newItems) => {
    setLocalMedia(
      newItems.map(({ id }, index) => ({
        ...localMedia.find((item) => item.id === id),
        position: index
      }))
    )
  }

  const handleMediaClick = (media) => {
    if (isMediaFileVideo(media)) {
      setSelectedMedia(media)
      setVideoModalOpen(true)
    }
  }

  const handleCrop = (media) => {
    setMediaToCrop(media)
    setCropModalOpen(true)
  }

  const handleCropComplete = async (croppedBlob) => {
    if (!croppedBlob || !mediaToCrop) return
    try {
      setIsLoading(true)
      const fileName = mediaToCrop.name || `cropped-${Date.now()}.jpg`
      const croppedFile = new File([croppedBlob], fileName, { type: "image/jpeg" })
      const croppedUrl = URL.createObjectURL(croppedFile)
      setLocalMedia((prev) =>
        prev.map((item) =>
          item.id === mediaToCrop.id
            ? { ...item, file_type: "cropped_photo", file: croppedFile, url: croppedUrl, thumbnail_url: croppedUrl, preview_url: undefined, file_preview: undefined }
            : item
        )
      )
      message.success(t("listings.tabs.media.image_updated_successfully"))
    } catch (error) {
      console.error("Error cropping image:", error)
      message.error(t("listings.tabs.media.crop_area_invalid"))
    } finally {
      setIsLoading(false)
      setCropModalOpen(false)
      setMediaToCrop(null)
    }
  }

  const handleVideoConfirm = async ({ media, blob, url, posterUrl, posterBlob }) => {
    if (!media) return
    try {
      setIsLoading(true)
      const trimmedFileName = media?.name ? media.name.replace(/\.[^/.]+$/, "") + "-trimmed.mp4" : `trimmed-${Date.now()}.mp4`
      const trimmedFile = blob ? new File([blob], trimmedFileName, { type: "video/mp4" }) : undefined
      setLocalMedia((prev) =>
        prev.map((item) =>
          item.id === media.id
            ? { ...item, file_type: "trimmed_video", file: trimmedFile ?? item.file, blob: posterBlob ?? item.blob, url: url ?? item.url, preview_url: posterUrl ?? item.preview_url, thumbnail_url: posterUrl ?? item.thumbnail_url, file_preview: undefined }
            : item
        )
      )
      message.success(t("listings.tabs.media.video_updated_successfully"))
    } catch (e) {
      console.error("Video update error", e)
      message.error(t("listings.tabs.media.video_trim_failed"))
    } finally {
      setIsLoading(false)
      setMediaToCrop(null)
    }
  }

  // Register validate handler — blocks Next if fewer than 2 active media items
  useEffect(() => {
    stepValidateHandlersRef.current["media"] = () => {
      if (activeMediaLength < 1) {
        dispatch(
          receiveErrors({
            data: { message: { listing_media: [t("listings.tabs.media.min_photos_error")] } },
            showToast: false
          })
        )
        const err = new Error(t("listings.tabs.media.min_photos_error"))
        err.errorFields = [{ errors: [t("listings.tabs.media.min_photos_error")] }]
        throw err
      }
    }
    return () => {
      delete stepValidateHandlersRef.current["media"]
    }
  }, [dispatch, stepValidateHandlersRef, activeMediaLength, t])

  // Register save handler
  useEffect(() => {
    stepSaveHandlersRef.current["media"] = async () => buildMediaPayload(localMedia)
    return () => {
      delete stepSaveHandlersRef.current["media"]
    }
  }, [stepSaveHandlersRef, localMedia])

  return (
    <Spin spinning={isLoading} size="large" style={{ width: "100%" }}>
      <div className={`flex-column ${style.mediaWrap}`}>
        <div className="position-relative">
          <MediaUpload
            className="mb-m block"
            imageInfo={t("media_upload.image_size_limit", { maxSize: MAX_MEDIA_SIZE.image })}
            videoInfo={t("media_upload.video_size_limit", { maxSize: MAX_MEDIA_SIZE.video })}
            onFileListChange={onFileListChange}
            lastPosition={activeMediaLength}
            maxCount={MAX_COUNT}
            maxCountErrorMessage={`${t("listings.tabs.media.max_photos_error")} ${MAX_COUNT}`}
            initialCount={activeMediaLength}
            disabled={isLoading}
          />
          {errors.listing_media && (
            <div className="pb-s">
              <CustomInputError errorKey={errors.listing_media[0]} />
            </div>
          )}
          {hasMedia && (
            <MediaGrid
              className="block"
              key={activeMediaLength}
              mediaItems={filteredMedia}
              withActions
              onDelete={handleDelete}
              onCrop={handleCrop}
              onReorder={handleReorder}
              lastPosition={activeMediaLength}
              onImageClick={handleMediaClick}
              sameSize
              showHeroImageLabel
              disabled={isLoading}
            />
          )}
          <VideoPlayerModal open={videoModalOpen} handleClose={() => { setVideoModalOpen(false); setSelectedMedia(null) }} selectedMedia={selectedMedia} />
          <MediaEditorModal
            visible={cropModalOpen}
            onClose={() => { setCropModalOpen(false); setMediaToCrop(null) }}
            media={mediaToCrop}
            onImageCropComplete={handleCropComplete}
            onVideoConfirm={handleVideoConfirm}
            targetWidth={1080}
            targetHeight={1080}
          />
        </div>
      </div>
    </Spin>
  )
}
